import {
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import {
	MatDialog,
	MatDialogRef,
	MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { FundraiserService } from 'src/app/pages/fundraiser/services/fundraiser.service';
import { MediaService } from 'src/app/pages/fundraiser/services/media.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { BackgroundImageEditDialogComponent } from './dialog/background-image-edit-dialog/background-image-edit-dialog/background-image-edit-dialog.component';
import { AddVideoBackgroundDialogComponent } from '../../../../pages/create-fundraiser/add-video-background-dialog/add-video-background-dialog.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass, NgIf, NgStyle } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';

@Component({
	selector: 'app-background-media',
	templateUrl: './background-media.component.html',
	styleUrls: ['./background-media.component.scss'],
	standalone: true,
	imports: [
		FlexModule,
		NgClass,
		ExtendedModule,
		MatButtonModule,
		MatFormFieldModule,
		NgIf,
		NgStyle,
	],
})
export class BackgroundMediaComponent implements OnInit {
	@Output() valueSelected = new EventEmitter<any>();
	defaultBackgroundImage =
		'https://imagedelivery.net/_0vgnXOEIHPwLg2E52a7gg/production/fundraiser_header/default/public';
	isMobileView: boolean = false;

	constructor(
		public dialogRef: MatDialogRef<BackgroundMediaComponent>,
		public _fundraiserService: FundraiserService,
		public dialog: MatDialog,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			imagePath: any;
			videoPath: any;
			backgroundImageId: any;
			fundraiserId: any;
			slug: any;
		},
		public notificationService: NotificationService,
		public mediaService: MediaService,
		private sanitizer: DomSanitizer,
		private breakpointObserver: BreakpointObserver
	) {}

	/**Detault background image when no image is uploaded */
	ngOnInit(): void {
		this.breakpointObserver
			.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
			.subscribe((result) => {
				this.isMobileView = result.matches;
			});
	}

	/**To open image cropping dialog */

	openVideoCropperDialog() {
		const dialogRef = this.dialog.open(AddVideoBackgroundDialogComponent, {
			data: {
				videoPath: this.data.videoPath,
				slug: this.data.slug,
				autoFocus: false,
			},
			width: '500px',
		});

		dialogRef.afterClosed().subscribe((update: any) => {
			if (update) {
				this.data.videoPath = update; // Update the video path
				this.valueSelected.emit(update); // Emit the value to the main component
				this.dialogRef.close();
			}
		});
	}
	openFileUploader() {
		const fileInput = document.getElementById(
			'upload_image_input'
		) as HTMLInputElement;

		if (fileInput) {
			// Add event listener to handle file selection
			const handleChange = (event: Event) => {
				const inputElement = event.target as HTMLInputElement;
				if (inputElement.files && inputElement.files.length > 0) {
					const selectedFile = inputElement.files[0];
					const mimeType = selectedFile.type;

					if (mimeType === 'image/gif') {
						this.mediaService
							.convertFileToBase64(selectedFile)
							.then((base64String) => {
								const backgroundPayload = {
									image: this.mediaService.base64toBlob(base64String),
									slug: this.data.slug,
								};
								let backgroundVideoPayload = {
									video: '',
									slug: this.data?.slug,
								};

								this.dialogRef.close();
								// Send it to the backend now
								this._fundraiserService
									.updateFundraiserBackground(backgroundPayload)
									.subscribe(
										(res) => {
											this._fundraiserService
												.addVideoBackground(backgroundVideoPayload)
												.then((res) => {
													this.notificationService.openNotification(
														$localize`:@@background_image_fundraiserBackground_notification:Fundraiser background image is saved`,
														'',
														'success'
													);
													window.location.reload();
												});
										},
										(err: any) => {
											this.notificationService.openNotification(
												$localize`:@@edit_created_errorUpdatingBackground_notification:There was an error updating background`,
												'close',
												'error'
											);
										}
									);
							});
					} else {
						const reader = new FileReader();
						reader.onload = (e: any) => {
							const img = new Image();
							img.src = e.target.result;

							img.onload = () => {
								const aspectRatio = img.width / img.height;
								const canvasAspect = 2 / 1;

								if (aspectRatio < canvasAspect) {
									// Apply blur and open dialog
									this.applyBlurAndOpenDialog(img);
								} else {
									// Normal flow
									const selectedFilePath = URL.createObjectURL(selectedFile);
									this.openEditDialog(selectedFilePath);
								}
							};
						};
						reader.readAsDataURL(selectedFile);
					}
				}

				// Cleanup
				fileInput.removeEventListener('change', handleChange);
				fileInput.value = '';
			};

			fileInput.addEventListener('change', handleChange);
			fileInput.click();
		}
	}

	applyBlurAndOpenDialog(img: HTMLImageElement) {
		const width = 1600;
		const height = 800;

		const svg = `
	<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
		<filter id="blurFilter">
			<feGaussianBlur in="SourceGraphic" stdDeviation="40" />
		</filter>
		<image x="0" y="0" width="${width}" height="${height}" href="${img.src}" filter="url(#blurFilter)" preserveAspectRatio="xMidYMid slice" />
	</svg>`;

		const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
		const svgUrl = URL.createObjectURL(svgBlob);

		// Load both blurred and original images in sequence
		const blurredImg = new Image();
		const originalImg = new Image();

		blurredImg.crossOrigin = 'anonymous';
		originalImg.crossOrigin = 'anonymous';

		blurredImg.onload = () => {
			originalImg.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext('2d');
				if (!ctx) return;

				// Draw blurred background
				ctx.drawImage(blurredImg, 0, 0, width, height);

				// Draw original image on top (centered)
				const ratio = Math.min(
					width / originalImg.width,
					height / originalImg.height
				);
				const newWidth = originalImg.width * ratio;
				const newHeight = originalImg.height * ratio;
				const offsetX = (width - newWidth) / 2;
				const offsetY = (height - newHeight) / 2;

				ctx.drawImage(originalImg, offsetX, offsetY, newWidth, newHeight);

				canvas.toBlob((blob) => {
					if (blob) {
						const finalFile = new File([blob], 'blurred.png', {
							type: 'image/png',
						});
						const finalUrl = URL.createObjectURL(finalFile);
						this.openEditDialog(finalUrl);
					}
					URL.revokeObjectURL(svgUrl);
				});
			};
			// Original image is loaded after blurred image
			originalImg.src = img.src;
		};

		// Start with the blurred version (SVG-based)
		blurredImg.src = svgUrl;
	}

	openEditDialog(imagePath: string) {
		this.dialog.open(BackgroundImageEditDialogComponent, {
			maxHeight: '98vh',
			data: {
				imagePath: imagePath,
				backgroundImageId: this.data.backgroundImageId,
				fundraiserId: this.data.fundraiserId,
				slug: this.data.slug,
				videoPath: this.data.videoPath,
			},
		});
	}

	///Old
	openImageCropperDialog() {
		this.dialog.open(BackgroundImageEditDialogComponent, {
			maxHeight: '98vh',
			data: {
				imagePath: this.data.imagePath,
				backgroundImageId: this.data.backgroundImageId,
				fundraiserId: this.data.fundraiserId,
				slug: this.data.slug,
				videoPath: this.data.videoPath,
			},
		});
	}
	onCloseClick() {
		this.dialogRef.close();
	}
}
